import React, { useState } from "react";
import Icon from "components/AppIcon";
import Input from "components/ui/Input";

const TOKEN_STANDARDS = [
  { value: "erc-3643", label: "ERC-3643 (T-REX)", description: "Institutional-grade security token standard with built-in compliance and identity management." },
  { value: "erc-1400", label: "ERC-1400", description: "Security token standard with partition-based ownership and transfer restrictions." },
  { value: "erc-1155", label: "ERC-1155", description: "Multi-token standard supporting fungible and non-fungible tokens in a single contract." },
  { value: "placeholder", label: "To Be Determined", description: "Token standard will be selected during technical implementation phase." },
];

const TRANSFER_RESTRICTIONS = [
  { value: "kyc-aml", label: "KYC/AML Verified Wallets Only", description: "Transfers restricted to wallets with verified identity and AML clearance." },
  { value: "accredited-only", label: "Accredited Investors Only", description: "Transfers limited to FCA-authorised or equivalent accredited investors." },
  { value: "whitelist", label: "Whitelist-Based", description: "Transfers permitted only between pre-approved wallet addresses." },
  { value: "lock-up", label: "Lock-Up Period Enforced", description: "Transfers blocked during defined lock-up period post-issuance." },
  { value: "jurisdiction", label: "Jurisdiction Restrictions", description: "Transfers restricted based on investor jurisdiction and regulatory requirements." },
];

function ToggleSwitch({ checked, onChange, label, description, disabled }) {
  return (
    <div className="flex items-start gap-4">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange(!checked)}
        className="relative flex-shrink-0 w-11 h-6 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 mt-0.5"
        style={{
          background: checked ? "var(--color-primary)" : "var(--color-border)",
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <span
          className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200"
          style={{ transform: checked ? "translateX(20px)" : "translateX(0)" }}
        />
      </button>
      <div className="flex-1 min-w-0">
        <p className="font-caption font-medium text-sm text-foreground">{label}</p>
        {description && (
          <p className="font-caption text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );
}

export default function StepTokenisation({ data, onChange, errors }) {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const toggleRestriction = (value) => {
    const current = data?.transferRestrictions || [];
    const updated = current?.includes(value)
      ? current?.filter((r) => r !== value)
      : [...current, value];
    handleChange("transferRestrictions", updated);
  };

  const isTokenisationEnabled = data?.tokenisationEnabled || false;

  return (
    <div className="space-y-6 lg:space-y-8">
      <div>
        <h3 className="font-heading text-lg lg:text-xl font-semibold text-foreground mb-1">
          Tokenisation Readiness
        </h3>
        <p className="font-caption text-sm text-muted-foreground">
          Configure RWA tokenisation parameters for future digital securities issuance. These settings are preparatory and do not constitute a live token issuance.
        </p>
      </div>
      {/* RWA Notice */}
      <div
        className="flex items-start gap-3 p-4 rounded-lg"
        style={{ background: "rgba(59,76,76,0.08)", border: "1px solid rgba(59,76,76,0.3)" }}
      >
        <Icon name="Sparkles" size={18} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-caption font-semibold text-sm text-foreground mb-1">
            Real World Asset (RWA) Tokenisation — Forward-Looking Configuration
          </p>
          <p className="font-caption text-xs text-muted-foreground leading-relaxed">
            SeedDrive&apos;s tokenisation module prepares your SPV for future digital securities issuance on regulated blockchain infrastructure. Configuration here establishes technical parameters for when regulatory approval and technical implementation are completed. This does not constitute a live token issuance or financial promotion.
          </p>
        </div>
      </div>
      {/* Master Toggle */}
      <div className="p-4 lg:p-6 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
        <ToggleSwitch
          checked={isTokenisationEnabled}
          onChange={(val) => handleChange("tokenisationEnabled", val)}
          label="Enable Tokenisation Readiness"
          description="Activate RWA tokenisation configuration for this SPV. Enables digital securities parameters and secondary trading readiness settings."
        />
      </div>
      {isTokenisationEnabled && (
        <>
          {/* Token Standard */}
          <div className="p-4 lg:p-6 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Code2" size={18} color="var(--color-primary)" />
              <h4 className="font-caption font-semibold text-foreground">Token Standard</h4>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-caption font-medium"
                style={{ background: "rgba(217,119,6,0.1)", color: "var(--color-warning)" }}>
                Placeholder
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {TOKEN_STANDARDS?.map((ts) => {
                const isSelected = data?.tokenStandard === ts?.value;
                return (
                  <button
                    key={ts?.value}
                    type="button"
                    onClick={() => handleChange("tokenStandard", ts?.value)}
                    className="flex items-start gap-3 p-3 rounded-lg border text-left transition-all duration-200"
                    style={{
                      borderColor: isSelected ? "var(--color-primary)" : "var(--color-border)",
                      background: isSelected ? "rgba(59,76,76,0.05)" : "transparent",
                    }}
                  >
                    <div
                      className="w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center"
                      style={{ borderColor: isSelected ? "var(--color-primary)" : "var(--color-border)" }}
                    >
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full" style={{ background: "var(--color-primary)" }} />
                      )}
                    </div>
                    <div>
                      <p className="font-caption font-semibold text-sm text-foreground">{ts?.label}</p>
                      <p className="font-caption text-xs text-muted-foreground mt-0.5">{ts?.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Blockchain Network (Placeholder)"
                type="text"
                placeholder="e.g. Ethereum Mainnet / Polygon"
                value={data?.blockchainNetwork || ""}
                onChange={(e) => handleChange("blockchainNetwork", e?.target?.value)}
                description="Target blockchain network for token deployment."
              />
              <Input
                label="Total Token Supply (Placeholder)"
                type="number"
                placeholder="e.g. 50000000"
                value={data?.tokenSupply || ""}
                onChange={(e) => handleChange("tokenSupply", e?.target?.value)}
                description="Total number of tokens representing fund units."
              />
            </div>
          </div>

          {/* Transfer Restrictions */}
          <div className="p-4 lg:p-6 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Lock" size={18} color="var(--color-primary)" />
              <h4 className="font-caption font-semibold text-foreground">Transfer Restrictions</h4>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-caption font-medium"
                style={{ background: "rgba(217,119,6,0.1)", color: "var(--color-warning)" }}>
                Placeholder
              </span>
            </div>
            <p className="font-caption text-xs text-muted-foreground mb-4">
              Select applicable transfer restrictions to be encoded in the smart contract. Multiple restrictions can be applied simultaneously.
            </p>
            <div className="space-y-3">
              {TRANSFER_RESTRICTIONS?.map((tr) => {
                const isSelected = (data?.transferRestrictions || [])?.includes(tr?.value);
                return (
                  <button
                    key={tr?.value}
                    type="button"
                    onClick={() => toggleRestriction(tr?.value)}
                    className="flex items-start gap-3 w-full p-3 rounded-lg border text-left transition-all duration-200"
                    style={{
                      borderColor: isSelected ? "var(--color-primary)" : "var(--color-border)",
                      background: isSelected ? "rgba(59,76,76,0.04)" : "transparent",
                    }}
                  >
                    <div
                      className="w-4 h-4 rounded border-2 flex-shrink-0 mt-0.5 flex items-center justify-center"
                      style={{
                        borderColor: isSelected ? "var(--color-primary)" : "var(--color-border)",
                        background: isSelected ? "var(--color-primary)" : "transparent",
                      }}
                    >
                      {isSelected && <Icon name="Check" size={10} color="white" strokeWidth={3} />}
                    </div>
                    <div>
                      <p className="font-caption font-medium text-sm text-foreground">{tr?.label}</p>
                      <p className="font-caption text-xs text-muted-foreground mt-0.5">{tr?.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Secondary Trading */}
          <div className="p-4 lg:p-6 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="ArrowLeftRight" size={18} color="var(--color-primary)" />
              <h4 className="font-caption font-semibold text-foreground">Secondary Trading Readiness</h4>
            </div>
            <div className="space-y-4">
              <ToggleSwitch
                checked={data?.secondaryTradingEnabled || false}
                onChange={(val) => handleChange("secondaryTradingEnabled", val)}
                label="Enable Secondary Trading Readiness"
                description="Prepare SPV structure for future secondary market trading of tokenised units."
              />
              {data?.secondaryTradingEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <label className="block font-caption text-sm font-medium text-foreground mb-1.5">
                      Secondary Market Venue (Placeholder)
                    </label>
                    <select
                      value={data?.secondaryVenue || ""}
                      onChange={(e) => handleChange("secondaryVenue", e?.target?.value)}
                      className="w-full h-10 px-3 rounded-md border text-sm font-caption bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      style={{ borderColor: "var(--color-border)" }}
                    >
                      <option value="">Select venue…</option>
                      <option value="izx">InvestaX (Singapore)</option>
                      <option value="archax">Archax (UK FCA Regulated)</option>
                      <option value="sdx">SDX (SIX Digital Exchange)</option>
                      <option value="tbd">To Be Determined</option>
                    </select>
                  </div>
                  <Input
                    label="Lock-Up Period (Months)"
                    type="number"
                    placeholder="e.g. 12"
                    value={data?.lockUpPeriod || ""}
                    onChange={(e) => handleChange("lockUpPeriod", e?.target?.value)}
                    description="Minimum holding period before secondary trading permitted."
                  />
                </div>
              )}
              <ToggleSwitch
                checked={data?.amlScreeningEnabled || false}
                onChange={(val) => handleChange("amlScreeningEnabled", val)}
                label="Automated AML Screening on Transfer"
                description="Integrate on-chain AML screening for all secondary market transfers."
              />
              <ToggleSwitch
                checked={data?.dividendAutomation || false}
                onChange={(val) => handleChange("dividendAutomation", val)}
                label="Automated Distribution via Smart Contract"
                description="Enable automated income distributions to token holders via smart contract execution."
              />
            </div>
          </div>

          {/* Readiness Status */}
          <div className="p-4 rounded-lg" style={{ background: "rgba(5,150,105,0.06)", border: "1px solid rgba(5,150,105,0.2)" }}>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
              <span className="font-caption font-semibold text-sm text-foreground">Tokenisation Readiness Status</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Legal Structure", status: "ready", icon: "Building2" },
                { label: "Token Standard", status: data?.tokenStandard ? "configured" : "pending", icon: "Code2" },
                { label: "Transfer Rules", status: (data?.transferRestrictions || [])?.length > 0 ? "configured" : "pending", icon: "Lock" },
                { label: "Secondary Market", status: data?.secondaryTradingEnabled ? "configured" : "pending", icon: "ArrowLeftRight" },
              ]?.map((item) => (
                <div key={item?.label} className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: item?.status === "ready" || item?.status === "configured" ?"rgba(5,150,105,0.15)" :"rgba(107,114,128,0.15)",
                    }}
                  >
                    <Icon
                      name={item?.status === "ready" || item?.status === "configured" ? "Check" : "Clock"}
                      size={12}
                      color={item?.status === "ready" || item?.status === "configured" ? "var(--color-success)" : "var(--color-muted-foreground)"}
                    />
                  </div>
                  <div>
                    <p className="font-caption text-xs font-medium text-foreground">{item?.label}</p>
                    <p className="font-caption text-xs" style={{
                      color: item?.status === "ready" || item?.status === "configured" ? "var(--color-success)" : "var(--color-muted-foreground)"
                    }}>
                      {item?.status === "ready" ? "Ready" : item?.status === "configured" ? "Configured" : "Pending"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {!isTokenisationEnabled && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
            style={{ background: "var(--color-muted)" }}>
            <Icon name="Coins" size={28} color="var(--color-muted-foreground)" />
          </div>
          <p className="font-caption font-medium text-foreground mb-1">Tokenisation Not Enabled</p>
          <p className="font-caption text-sm text-muted-foreground max-w-sm">
            Toggle &ldquo;Enable Tokenisation Readiness&rdquo; above to configure RWA digital securities parameters for this SPV.
          </p>
        </div>
      )}
    </div>
  );
}